import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Box,
  FilledInput,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Paper,
  Theme,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { Send } from '@material-ui/icons';
import moment from 'moment';
import firebase from 'firebase/app';
import 'firebase/firestore';

import UserContext from '../contexts/UserContext';
import { database, fieldValue } from '../services/firebase';
import { cleanText } from '../services/rude-word';

export type Props = {
  item: any;
};

const useStyles = makeStyles<Theme>((theme) => ({
  avatar: {
    alignSelf: 'flex-end',
    marginRight: theme.spacing(1),
    width: theme.spacing(4),
    height: theme.spacing(4),
  },
  chatBody: {
    borderStyle: 'solid',
    borderColor: '#707070',
    borderWidth: '1px 0px 1px 0px',
    height: 500,
    overflow: 'auto',
    padding: theme.spacing(2),
  },
  chatBox: {
    backgroundColor: '#454057',
    borderRadius: 8,
    paddingBottom: 16,
  },
  chatBoxTitle: {
    borderRadius: '8px 8px 0px 0px',
    padding: `16px`,
  },
  input: {
    color: '#CCC',
  },
  otherBubble: {
    display: 'flex',
    paddingBottom: theme.spacing(1),
  },
  otherPaper: {
    backgroundColor: '#564F70',
    maxWidth: theme.spacing(18),
    padding: theme.spacing(1),
  },
  userBubble: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingBottom: theme.spacing(1),
  },
  userPaper: {
    backgroundColor: '#46A19F',
    maxWidth: theme.spacing(18),
    padding: theme.spacing(1),
  },
  chatText: { wordBreak: 'break-word', color: 'white' },
  timeText: {
    alignSelf: ' flex-end',
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    color: '#9C9FAE',
  },
  nameText: {
    color: '#9C9FAE',
  },
  nonameText: { color: '#6d6f7a' },
}));

const itemPerFetch = 100;

const ChatBox = () => {
  const mesRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadedAll, setLoadedAll] = useState(false);
  const [text, setText] = useState<string>('');
  const [list, setList] = useState<any[]>([]);
  const { state } = useContext(UserContext);
  const { chatBody, chatBox, chatBoxTitle, input } = useStyles();

  useEffect(() => {
    let items: any[] = [];
    let unsubscribe: any = null;
    database
      .collection('chat')
      .orderBy('createdAt', 'desc')
      .limit(itemPerFetch + 1)
      .get()
      .then(
        (
          snapshot: firebase.firestore.QuerySnapshot<
            firebase.firestore.DocumentData
          >
        ) => {
          items = snapshot.docs.map(
            (
              doc: firebase.firestore.QueryDocumentSnapshot<
                firebase.firestore.DocumentData
              >
            ) => ({ ...doc.data(), id: doc.id, ref: doc })
          );
          items.reverse();
          if (items.length > itemPerFetch) {
            items.splice(0, 1);
          } else {
            setLoadedAll(true);
          }
          setList(items);
        }
      )
      .then(() => {
        unsubscribe = database
          .collection('chat')
          .orderBy('createdAt', 'desc')
          .limit(1)
          .onSnapshot(
            (
              snapshot: firebase.firestore.QuerySnapshot<
                firebase.firestore.DocumentData
              >
            ) => {
              let newItems: any[] = [];
              let changeItems: any[] = [];
              snapshot
                .docChanges()
                .forEach(
                  (
                    change: firebase.firestore.DocumentChange<
                      firebase.firestore.DocumentData
                    >
                  ) => {
                    if (change.type === 'added') {
                      newItems.push({
                        ...change.doc.data(),
                        id: change.doc.id,
                        ref: change.doc,
                      });
                    } else if (change.type === 'modified') {
                      changeItems.push({
                        ...change.doc.data(),
                        id: change.doc.id,
                        ref: change.doc,
                      });
                    }
                  }
                );
              setList((oldList: any[]) => {
                let list: any[] = [...oldList];
                newItems.forEach((item) => {
                  if (list.findIndex((lItem) => lItem.id === item.id) < 0) {
                    list.push(item);
                  }
                });
                changeItems.forEach((item) => {
                  const index = list.findIndex(
                    (lItem: any) => lItem.id === item.id
                  );
                  if (index >= 0) {
                    list[index] = { ...item };
                  }
                });

                return list;
              });
            }
          );
      });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (mesRef && mesRef.current) {
      mesRef.current.scrollTop = mesRef.current.scrollHeight;
    }
  }, [list]);

  const onScrollChange = (e: React.UIEvent<HTMLElement>) => {
    if (
      !loadedAll &&
      e.currentTarget.scrollTop === 0 &&
      !loadingMore &&
      !loading
    ) {
      loadMore();
    }
  };

  const loadMore = async () => {
    setLoadingMore(true);
    const snapshot = await database
      .collection('chat')
      .orderBy('createdAt', 'desc')
      .startAfter(list[0].ref)
      .limit(itemPerFetch + 1)
      .get();
    const items = snapshot.docs.map(
      (
        doc: firebase.firestore.QueryDocumentSnapshot<
          firebase.firestore.DocumentData
        >
      ) => ({ ...doc.data(), id: doc.id, ref: doc.ref })
    );
    items.reverse();
    if (items.length > itemPerFetch) {
      items.splice(0, 1);
    } else {
      setLoadedAll(true);
    }
    if (items.length > 0) {
      setList((oldList) => [...items, ...oldList]);
    }

    setLoadingMore(false);
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setText(value);
  };

  const onSubmit = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    const cleanMsg = await cleanText(text);
    await database.collection('chat').add({
      message: cleanMsg,
      createdAt: fieldValue.serverTimestamp(),
      name: state.user?.displayName,
      senderId: state.user?.uid,
      avatar: state.user?.photoUrl,
    });
    setText('');
    setLoading(false);
  };
  return (
    <Box className={chatBox}>
      <Typography className={chatBoxTitle}>ห้องแชท</Typography>
      <div className={chatBody} ref={mesRef} onScroll={onScrollChange}>
        {list.map((item: any, index: number) => {
          if (item.senderId === state.user?.uid) {
            return <UserBubble key={index} item={item} />;
          } else {
            return <OtherBubble key={index} item={item} />;
          }
        })}
      </div>
      <Box px={2} mt={1}>
        <form onSubmit={onSubmit}>
          <FormControl variant="filled" size="small" fullWidth>
            <InputLabel>แชทที่นี่</InputLabel>
            <FilledInput
              color="primary"
              classes={{ input }}
              disabled={!state.user || loading}
              value={text}
              onChange={onInputChange}
              fullWidth
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    color="secondary"
                    edge="end"
                    onClick={onSubmit}
                    disabled={loading || state.user === null || text === ''}
                  >
                    <Send />
                  </IconButton>
                </InputAdornment>
              }
            />
          </FormControl>
        </form>
      </Box>
    </Box>
  );
};

const OtherBubble: React.FC<Props> = ({ item }) => {
  const {
    avatar,
    chatText,
    nameText,
    nonameText,
    timeText,
    otherBubble,
    otherPaper,
  } = useStyles();
  return (
    <div className={otherBubble}>
      <Avatar
        className={avatar}
        src={item.avatar || '/images/placeholder.png'}
      />
      <div>
        <Typography
          className={item.name && item.name !== '' ? nameText : nonameText}
          variant="caption"
        >
          {item.name && item.name !== '' ? item.name : 'ไม่มีชื่อ'}
        </Typography>
        <Paper className={otherPaper}>
          <Typography className={chatText}>{item.message}</Typography>
        </Paper>
      </div>
      <Typography className={timeText} variant="body2">
        {item.createdAt && moment(item.createdAt.toDate()).format('HH:mm')}
      </Typography>
    </div>
  );
};

const UserBubble: React.FC<Props> = ({ item }) => {
  const { chatText, timeText, userBubble, userPaper } = useStyles();
  return (
    <div className={userBubble}>
      <Typography className={timeText} variant="body2">
        {item.createdAt && moment(item.createdAt.toDate()).format('HH:mm')}
      </Typography>
      <Paper className={userPaper}>
        <Typography className={chatText}>{item.message}</Typography>
      </Paper>
    </div>
  );
};

export default ChatBox;
